export const REDACTED_VALUE = '[REDACTED]';

export type RedactionOptions = {
  redactPromptContent?: boolean;
};

const SENSITIVE_KEY_PATTERN = /(^|[_-])(secret|secrets|token|tokens|password|passwd|pwd|api[_-]?key|private[_-]?key|client[_-]?secret|webhook[_-]?secret|authorization|auth[_-]?header|access[_-]?token|refresh[_-]?token|credential|credentials)($|[_-])/i;
const BEARER_TOKEN_PATTERN = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;
const GITHUB_TOKEN_PATTERN = /\bgh[opsru]_[A-Za-z0-9_]+\b/g;
const OPENAI_TOKEN_PATTERN = /\bsk-[A-Za-z0-9_-]+\b/g;
const INLINE_SECRET_PATTERN = /\b(api[_-]?key|password|passwd|pwd|secret|token|access[_-]?token|refresh[_-]?token)\s*[:=]\s*[^\s,;]+/gi;
const PRIVATE_KEY_PATTERN = /-----BEGIN [^-]*PRIVATE KEY-----[\s\S]*?-----END [^-]*PRIVATE KEY-----/g;

const SENSITIVE_KEYS = new Set([
  'secretrefs',
  'secret_refs',
  'clientsecret',
  'webhooksecret',
  'apikey',
  'api_key',
  'headers',
  'cookies',
  'cookie',
  'env',
  'env_values',
  'environment',
  'environment_values',
  'environment_variables',
]);
const PROMPT_CONTENT_KEYS = new Set([
  'body',
  'content',
  'custom_instructions',
  'custominstructions',
  'default_content',
  'developer_instructions',
  'developer_prompt',
  'developer_prompt_content',
  'effective_developer_instructions',
  'instructions',
  'prompt',
  'prompt_body',
  'prompt_content',
  'prompt_text',
  'system_prompt',
]);

export function isSensitiveKey(key: unknown) {
  const rawName = String(key ?? '');
  const name = rawName.toLowerCase();
  const segmentedName = segmentKeyName(rawName);
  return SENSITIVE_KEY_PATTERN.test(name)
    || SENSITIVE_KEY_PATTERN.test(segmentedName)
    || SENSITIVE_KEYS.has(name)
    || SENSITIVE_KEYS.has(segmentedName);
}

export function redactSensitive<T>(value: T, key?: string): T | string {
  if (isSensitiveKey(key) && value !== undefined && value !== null && value !== '') {
    return REDACTED_VALUE;
  }
  if (Array.isArray(value)) {
    return value.map(item => redactSensitive(item, key)) as T;
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([childKey, childValue]) => [
        childKey,
        redactSensitive(childValue, childKey),
      ]),
    ) as T;
  }
  return value;
}

export function redactSensitiveText(value: string) {
  return value
    .replace(PRIVATE_KEY_PATTERN, REDACTED_VALUE)
    .replace(BEARER_TOKEN_PATTERN, `Bearer ${REDACTED_VALUE}`)
    .replace(GITHUB_TOKEN_PATTERN, REDACTED_VALUE)
    .replace(OPENAI_TOKEN_PATTERN, REDACTED_VALUE)
    .replace(INLINE_SECRET_PATTERN, match => {
      const [prefix] = match.split(/[:=]/, 1);
      const separator = match.includes('=') ? '=' : ':';
      return `${prefix}${separator}${REDACTED_VALUE}`;
    });
}

export function redactForDisplay<T>(value: T, key?: string, options: RedactionOptions = {}): T | string {
  if (isSensitiveKey(key) && value !== undefined && value !== null && value !== '') {
    return REDACTED_VALUE;
  }
  if (options.redactPromptContent && isPromptContentKey(key) && value !== undefined && value !== null && value !== '') {
    return REDACTED_VALUE;
  }
  if (typeof value === 'string') {
    return redactSensitiveText(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map(item => redactForDisplay(item, key, options)) as T;
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([childKey, childValue]) => [
        childKey,
        redactForDisplay(childValue, childKey, options),
      ]),
    ) as T;
  }
  return value;
}

export function redactedJson(value: unknown, options: RedactionOptions = {}) {
  return JSON.stringify(redactForDisplay(value, undefined, options), null, 2);
}

export function redactPromptContentForDisplay<T>(value: T): T | string {
  return redactForDisplay(value, undefined, { redactPromptContent: true });
}

export function isPromptContentKey(key: unknown) {
  const name = String(key ?? '').toLowerCase();
  return PROMPT_CONTENT_KEYS.has(name) || /(^|[_-])prompt[_-]?content($|[_-])/.test(name);
}

function segmentKeyName(key: string) {
  return key
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .toLowerCase();
}

export function mergeRedactedDraft(original: unknown, edited: unknown, key?: string): unknown {
  if (isSensitiveKey(key) && edited === REDACTED_VALUE) return original;
  if (Array.isArray(edited)) {
    const originalArray = Array.isArray(original) ? original : [];
    return edited.map((item, index) => mergeRedactedDraft(originalArray[index], item, key));
  }
  if (edited && typeof edited === 'object') {
    const originalObject = original && typeof original === 'object' ? original as Record<string, unknown> : {};
    return Object.fromEntries(
      Object.entries(edited as Record<string, unknown>).map(([childKey, childValue]) => [
        childKey,
        mergeRedactedDraft(originalObject[childKey], childValue, childKey),
      ]),
    );
  }
  return edited;
}
